import { createContext, useContext, useEffect, type ComponentType } from 'react';
import { emptyTurnContext, type NativeSelectionAttachment } from '../types';
import { useCompany } from '@/company/context';
import { CompanyRequestError } from '@/company/api';
import { useWorkspace } from '../context';
import { ErrorState } from '../primitives';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from './routes';

export type ArtifactCanvasRenderer = ComponentType<{
  target: ArtifactCanvasTarget | DraftCanvasTarget;
  /** Presentation scope only; the server derives and rechecks authority. */
  companyId: string;
  actorId: string;
  /** Navigates to an exact named row; the saved reader rechecks current authority. */
  openVersion?: (target: ArtifactCanvasTarget) => void;
  /** Navigates only to the actual admitted private draft returned by Edit. */
  openEditor?: (target: DraftCanvasTarget) => void;
  /** Open the ordinary current/new composer without submitting a turn. */
  revealComposer?: (selection: NativeSelectionAttachment) => void;
}>;

export function ArtifactCanvasOutlet({
  target,
}: {
  target: ArtifactCanvasTarget | DraftCanvasTarget;
}) {
  const workspace = useWorkspace();
  const companyId = useCompany().data?.company?.id;
  const Renderer = workspace.artifactCanvas;
  if (!Renderer || !companyId || !workspace.user)
    return (
      <ErrorState message={new CompanyRequestError(503, 'artifact_service_unavailable').message} />
    );
  return (
    <Renderer
      target={target}
      companyId={companyId}
      actorId={workspace.user.id}
      revealComposer={(nativeSelection) => {
        // Let the existing chat controller choose a real new/current composer. A local
        // unsent threadId is not a durable thread link. Keep the editor and its selection.
        workspace.send(
          '',
          emptyTurnContext(),
          false,
          { ...target, full: false },
          { newChat: true, nativeSelection },
        );
      }}
      openVersion={(version) => {
        workspace.openCanvas(version);
      }}
      openEditor={(editor) => {
        workspace.openCanvas(editor);
      }}
    />
  );
}

/** The artifact canvas names its own kind in the canvas header ("Document", not "Artifact"). */
export const CanvasKindLabelContext = createContext<(label: string | null) => void>(() => {});
export function useCanvasKindLabel(label: string | null) {
  const report = useContext(CanvasKindLabelContext);
  useEffect(() => {
    report(label);
    return () => report(null);
  }, [label, report]);
}

/** Any saved item reports its own name for the chat's "In this chat" list (not the page header). */
export const CanvasItemNameContext = createContext<(name: string | null) => void>(() => {});
export function useCanvasItemName(name: string | null | undefined) {
  const report = useContext(CanvasItemNameContext);
  useEffect(() => {
    report(name?.trim() ? name.trim() : null);
    return () => report(null);
  }, [name, report]);
}

/** A saved App reports its own name so the page header can show it ("Delivery map"), never a kind label. */
export const CanvasTitleContext = createContext<(title: string | null) => void>(() => {});
export function useCanvasTitle(title: string | null) {
  const report = useContext(CanvasTitleContext);
  useEffect(() => {
    report(title);
    return () => report(null);
  }, [title, report]);
}
